import { ApiError, apiBaseUrl, authToken } from "./client";
import { api } from "./client";

// ── Types ───────────────────────────────────────────────────────────

export interface SubjectSummary {
  id: string;
  name: string;
  examType: string;
  questionCount: number;
}

export interface StatsResponse {
  totalQuestions: number;
  subjects: SubjectSummary[];
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  /** Unique students who installed the PWA. Absent on older backends. */
  appInstalls?: { total: number; byPlatform: Record<string, number> };
}

export interface Topic {
  id: string;
  name: string;
  description?: string | null;
}

export interface SubTopic {
  id: string;
  name: string;
}

export interface QuestionListItem {
  id: string;
  questionText: string;
  questionType: string;
  options: Record<string, string> | null;
  correctAnswer: string | null;
  explanation: string | null;
  difficultyLevel: number | null;
  examType: string | null;
  examYear: number | null;
  questionSource: string | null;
  status: string;
  subject: { id: string; name: string } | null;
  topic: { id: string; name: string } | null;
  subTopic: { id: string; name: string } | null;
  imageUrl: string | null;
  passage: { id: string; content: string } | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionsPage {
  questions: QuestionListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ActivityEntry {
  id: string;
  action: string;
  targetId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  worker: { id: string; firstName: string | null; email: string | null };
}

// ── API calls ───────────────────────────────────────────────────────

export const fetchStats = () => api<StatsResponse>("/api/worker/stats");

export const fetchSubjects = () =>
  api<{
    subjects: {
      id: string;
      name: string;
      exam_type: string;
      is_mandatory: boolean;
      description: string | null;
    }[];
  }>("/api/worker/subjects");

export const fetchTopics = (subjectId: string) =>
  api<{ topics: Topic[] }>(`/api/worker/subjects/${subjectId}/topics`);

export const createTopic = (subjectId: string, name: string, description?: string) =>
  api<Topic>(`/api/worker/subjects/${subjectId}/topics`, {
    method: "POST",
    body: { name, description },
  });

export const fetchSubtopics = (topicId: string) =>
  api<{ subtopics: SubTopic[] }>(`/api/worker/topics/${topicId}/subtopics`);

export const createSubtopic = (topicId: string, name: string) =>
  api<SubTopic>(`/api/worker/topics/${topicId}/subtopics`, {
    method: "POST",
    body: { name },
  });

export const updateTopic = (id: string, name: string, description?: string) =>
  api<Topic>(`/api/worker/topics/${id}`, {
    method: "PUT",
    body: { name, description },
  });

export const deleteTopic = (id: string) =>
  api<{ ok: true }>(`/api/worker/topics/${id}`, { method: "DELETE" });

export const updateSubtopic = (id: string, name: string) =>
  api<SubTopic>(`/api/worker/subtopics/${id}`, {
    method: "PUT",
    body: { name },
  });

export const deleteSubtopic = (id: string) =>
  api<{ ok: true }>(`/api/worker/subtopics/${id}`, { method: "DELETE" });

export const fetchQuestions = (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  return api<QuestionsPage>(`/api/worker/questions?${qs}`);
};

export const fetchQuestion = (id: string) => api<QuestionListItem>(`/api/worker/questions/${id}`);

export const createQuestion = (body: Record<string, unknown>) =>
  api<{ id: string }>("/api/worker/questions", { method: "POST", body });

export const updateQuestion = (id: string, body: Record<string, unknown>) =>
  api<{ ok: true }>(`/api/worker/questions/${id}`, { method: "PUT", body });

export const deleteQuestion = (id: string) =>
  api<{ ok: true }>(`/api/worker/questions/${id}`, { method: "DELETE" });

export const bulkDeleteQuestions = (ids: string[]) =>
  api<{ deleted: number }>("/api/worker/questions/bulk-delete", {
    method: "POST",
    body: { ids },
  });

/** Must match `BULK_UPLOAD_MAX` on the backend. */
export const BULK_UPLOAD_BATCH = 500;

/**
 * Upload any number of questions, in batches the server accepts.
 *
 * The server caps one request at 500 rows. Batches go one after another, so a
 * failure part-way leaves the earlier batches saved and duplicates are skipped
 * if the worker uploads the same file again.
 */
export async function bulkUploadQuestions(
  questions: Record<string, unknown>[],
): Promise<{ created: number; skipped: number }> {
  let created = 0;
  let skipped = 0;
  for (let start = 0; start < questions.length; start += BULK_UPLOAD_BATCH) {
    const batch = questions.slice(start, start + BULK_UPLOAD_BATCH);
    const result = await api<{ created: number; skipped: number }>("/api/worker/questions/upload", {
      method: "POST",
      body: { questions: batch },
    });
    created += result.created;
    skipped += result.skipped;
  }
  return { created, skipped };
}

export const fetchActivity = (page = 1, limit = 30) =>
  api<{ entries: ActivityEntry[]; total: number; page: number; limit: number }>(
    `/api/worker/activity?page=${page}&limit=${limit}`,
  );

/** The largest image a question can carry; the backend refuses anything bigger. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];

/**
 * Upload one question image to storage (S3) and get back its link, which is
 * then saved on the question as `imageUrl`. The file is the raw body.
 */
export async function uploadQuestionImage(file: File): Promise<string> {
  const token = authToken.get();
  const response = await fetch(`${apiBaseUrl}/api/worker/images`, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });
  const payload = (await response.json().catch(() => null)) as {
    url?: string;
    error?: { code?: string; message?: string };
  } | null;
  if (!response.ok || !payload?.url) {
    throw new ApiError(
      response.status,
      payload?.error?.code ?? "UNKNOWN",
      payload?.error?.message ?? "Couldn't upload the image.",
    );
  }
  return payload.url;
}
